// https://vite-react-ssg.netlify.app/docs/getting-started
import { ViteReactSSG } from "vite-react-ssg";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DownloadProgress } from "@/components/common/download-progress";
import { MiutaApp } from "@/components/common/miuta-app";
import { Index } from "@/components/index/index";
import { Creation } from "@/components/creation";
import { CreationProduct } from "@/components/creation-product";
import { CreationFolder } from "@/components/creation-folder";
import { CreationProject } from "@/components/creation-project";
import { Settings } from "@/components/settings";
import { SettingsAccount } from "@/components/settings-account";
import { SettingsMembers } from "@/components/settings-members";
import { SettingsSubscription } from "@/components/settings-subscription";
import { Login } from "@/components/login";
import { Invite } from "@/components/invite";
import analytics from "@/utils/analytics";
import { Assets } from "@/components/assets";
import { AssetsActor } from "@/components/assets-actor";
import { AssetsVoice } from "@/components/assets-voice";
import { Agent } from "@/components/agent";
import { AgentDashboard } from "@/components/agent-dashboard";
import { UserManagement } from "@/components/agent-dashboard-user-management";
import { Workflow } from "@/components/agent-dashboard-workflow";
import { SubscriptionSettings } from "@/components/agent-dashboard-subscription-settings";
import "./locales";
import "./index.css";
import { AgentDashboardUserDetail } from "./components/agent-dashboard-user-detail";
import { SubscriptionPlan } from "./components/subscription-plan";
import { AgentCreation } from "./components/agent-creation";
import { AgentDashboardPermission } from "./components/agent-dashboard-permission";
import { AgentDashboardDataOverview } from "./components/agent-dashboard-data-overview";
import { AgentDashboardContentManagement } from "./components/agent-dashboard-content-management";
import { AgentDashboardOrderManagement } from "./components/agent-dashboard-order-management";
import { AssetsMaterial } from "./components/assets-material";
import { AssetsKnowledge } from "./components/assets-knowledge";
import { AssetsKnowledgeItem } from "./components/assets-knowledge-item";
import { AssetsMaterialItem } from "./components/assets-material-item";
import { UploadProgress } from "./components/common/upload-progress";
import { UploadProvider } from "./components/common/upload-context";
import { UnSubscribePage } from "./components/unsubscribe-page";
import { CompanyAsset } from "./components/agent-dashboard-company-asset";
import { OperationLog } from "./components/agent-dashboard-operation-log";
import { SelectSystemPage } from "./components/select-system-page";
import { StyleEditorMicroAppWebviewPage } from "./components/style-editor-microapp";
import { seedResolvedAgentFromSSR } from "./utils/ssr-agent-resolve";

const queryClient = new QueryClient();

// 尽可能早地将 SSR 注入的智能体解析结果写入 manager，
// 避免首屏其他 hook 先触发 resolveAgentId() 发起额外请求。
seedResolvedAgentFromSSR();

analytics.init();

const Layout = ({ children }: { children?: React.ReactNode }) => (
  <MiutaApp>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <UploadProvider>
          <Toaster />
          <Sonner />
          <DownloadProgress />
          <UploadProgress />
          {children}
        </UploadProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </MiutaApp>
);

const creation = {
  element: <Creation />,
  children: [
    // {
    //   path: "/creation/product/",
    //   element: <CreationProduct />,
    // },
    {
      path: "/creation/folder/:id/",
      element: <CreationFolder />,
    },
    {
      path: "/creation/project/:id/",
      element: <CreationProject />,
    },
  ],
};

const assets = {
  children: [
    {
      path: "/assets/material/",
      element: <AssetsMaterial />,
    },
    {
      path: "/assets/material/:id/",
      element: <AssetsMaterialItem />,
    },
    {
      path: "/assets/knowledge/",
      element: <AssetsKnowledge />,
    },
    {
      path: "/assets/knowledge/:id/",
      element: <AssetsKnowledgeItem />,
    },
    {
      path: "/assets/actor/",
      element: <AssetsActor />,
    },
    {
      path: "/assets/voice/",
      element: <AssetsVoice />,
    },
  ],
}

// 智能体
const agent = {
  path: "/agent/",
  // element: <Agent />,
  children: [
    // Agent 创作页
    {
      path: "/agent/creation/:id/",
      element: <AgentCreation />,
    },
  ],
}

// 智能体未 订阅页
const agentUnSubscribePage = {
  path: "/unsubscribe/",
  element: <UnSubscribePage />,
}

const agentDashBoard = {
  path: "/agent/dashboard/",
  element: (
    <Layout>
      <AgentDashboard />
    </Layout>
  ),
  children: [
    {
      path: "/agent/dashboard/user-management/:id/",
      element: <UserManagement />,
    },
    {
      path: "/agent/dashboard/workflow/:id/",
      element: <Workflow />,
    },
    {
      path: "/agent/dashboard/subscription-settings/:id/",
      element: <SubscriptionSettings />,
    },
    // 智能体二期
    {
      path: "/agent/dashboard/permission/:id/",
      element: <AgentDashboardPermission />,
    },
    {
      path: "/agent/dashboard/data-overview/:id/",
      element: <AgentDashboardDataOverview />,
    },
    {
      path: "/agent/dashboard/content-management/:id/",
      element: <AgentDashboardContentManagement />,
    },
    {
      path: "/agent/dashboard/order-management/:id/",
      element: <AgentDashboardOrderManagement />,
    },
    {
      path: "/agent/dashboard/company-asset/:id/",
      element: <CompanyAsset />,
    },
    {
      path: "/agent/dashboard/operation-log/:id/",
      element: <OperationLog />,
    },
  ],
}

const agentDashBoardUserManagementDetail = {
  path: "/agent/dashboard/user-management/:id/:userId",
  element: (
    <Layout>
      <AgentDashboardUserDetail />
    </Layout>
  ),
}

const settings = {
  element: <Settings />,
  children: [
    {
      path: "/settings/account/",
      element: <SettingsAccount />,
    },
    // {
    //   path: "/settings/members/",
    //   element: <SettingsMembers />,
    // },
    // {
    //   path: "/settings/subscription/",
    //   element: <SettingsSubscription />,
    // },
  ],
};

export const routes = [
  {
    path: "/",
    element: (
      <Layout>
        <Index />
      </Layout>
    ),
    // 暂时隐藏掉 creation
    children: [settings, assets, agent, agentUnSubscribePage],
  },
  {
    path: "/login/",
    element: (
      <Layout>
        <Login />
      </Layout>
    ),
  },
  {
    path: "/select-system/",
    element: (
      <Layout>
        <SelectSystemPage />
      </Layout>
    ),
  },
  // {
  //   path: "/subscription-plan/:state?",
  //   element: (
  //     <Layout>
  //       <SubscriptionPlan />
  //     </Layout>
  //   ),
  // },
  // 智能体运营后台新开页面
  {
    ...agentDashBoard,
  },
  {
    ...agentDashBoardUserManagementDetail,
  },
  {
    path: "/style-editor-microapp/:id/",
    element: <StyleEditorMicroAppWebviewPage />,
  },
  {
    path: "/invite/",
    element: (
      <Layout>
        <Invite />
      </Layout>
    ),
  },
  {
    path: "*",
    element: (
      <Layout>
        <div></div>
      </Layout>
    ),
  },
];

export const createRoot = ViteReactSSG({
  routes,
});
